import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import styles from './ContactInformations.module.sass';
import Card from '../../../components/Card';
import TextInput from '../../../components/TextInput';
import PhoneInput from '../../../components/PhoneInput';
import Icon from '../../../components/Icon';
import Editor from '../../../components/Editor';
import Dropdown from '../../../components/Dropdown';
import { EditorState, convertFromRaw, ContentState } from 'draft-js';

const ContactInformations = ({ company, onCompanyChange, onDropdownChange, loading }) => {
    const WORK_MODE_OPTIONS = [
        { value: 'Présentiel', title: 'Présentiel' },
        { value: 'Hybride', title: 'Hybride' },
        { value: 'Télétravail', title: 'Télétravail' }
    ];
    
    // Utiliser useRef pour stocker l'EditorState et éviter les re-rendus inutiles
    const editorStateRef = React.useRef(null);

    // Initialiser l'EditorState une seule fois
    const [content, setContent] = useState(() => {
        const missions = company?.main_missions;
        let editorState;

        if (missions) {
            try {
                // Essayer d'abord de parser comme JSON Draft.js
                const parsed = JSON.parse(missions);
                editorState = EditorState.createWithContent(convertFromRaw(parsed));
            } catch (e) {
                // Si ce n'est pas du JSON, traiter comme du texte simple
                editorState = EditorState.createWithContent(ContentState.createFromText(missions));
            }
        } else {
            editorState = EditorState.createEmpty();
        }

        editorStateRef.current = editorState;
        return editorState;
    });

    // Mettre à jour le contenu des missions quand la company change
    useEffect(() => {
        if (company?.main_missions && editorStateRef.current) {
            const currentContent = editorStateRef.current.getCurrentContent().getPlainText();
            const newMissions = company.main_missions;

            let newContent;
            try {
                const parsed = JSON.parse(newMissions);
                newContent = EditorState.createWithContent(convertFromRaw(parsed)).getCurrentContent().getPlainText();
            } catch (e) {
                newContent = newMissions;
            }

            // Ne mettre à jour que si le contenu a réellement changé
            if (newContent !== currentContent && !editorStateRef.current.getSelection().hasFocus) {
                let newState;
                try {
                    const parsed = JSON.parse(newMissions);
                    newState = EditorState.createWithContent(convertFromRaw(parsed));
                } catch (e) {
                    newState = EditorState.createWithContent(ContentState.createFromText(newMissions));
                }
                editorStateRef.current = newState;
                setContent(newState);
            }
        }
    }, [company?.main_missions]);

    // Fonction personnalisée pour mettre à jour le contenu de l'éditeur
    const handleEditorChange = (newEditorState) => {
        editorStateRef.current = newEditorState;
        setContent(newEditorState);

        // Convertir le contenu en texte simple pour la sauvegarde
        const missions = newEditorState.getCurrentContent().getPlainText();
        onCompanyChange({ target: { name: 'main_missions', value: missions } });
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    };
    return (
        <Card className={styles.card} title="Detail de la mission" classTitle="title-green">
                <TextInput
                    className={styles.field}
                    label="Intitulé du poste *"
                    name="position_title"
                    type="text"
                    value={company?.position_title || ''}
                    onChange={onCompanyChange}
                    required
                />
                
                <div className={styles.field}>
                    <label>Missions principales</label>
                    <Editor
                        state={content}
                        onChange={handleEditorChange}
                        classEditor={styles.editor}
                        placeholder="Décrivez les missions principales du poste"
                    />
                </div>
             
             <div className={styles.group}>
                <TextInput
                    className={styles.field}
                    label="Date de début *"
                    name="start_date"
                    type="date"
                    value={formatDateForInput(company?.start_date)}
                    onChange={onCompanyChange}
                    required
                />
                <TextInput
                    className={styles.field}
                    label="Date de fin *"
                    name="end_date"
                    type="date"
                    value={formatDateForInput(company?.end_date)}
                    onChange={onCompanyChange}
                    required
                />
                <TextInput
                    className={styles.field}
                    label="Lieu principal (si différent du siège)"
                    name="work_location"
                    type="text"
                    value={company?.work_location || ''}
                    onChange={onCompanyChange}
                />
                <div className={styles.field}>
                    <label>Mode de travail *</label>
                    <Dropdown
                        options={WORK_MODE_OPTIONS}
                        value={WORK_MODE_OPTIONS.find(opt => opt.value === company?.work_mode) || ''}
                        setValue={(option) => onDropdownChange('work_mode', option)}
                        placeholder="Présentiel / Hybride / Télétravail"
                    />
                </div>
            </div>

             <div className={styles.divider}></div>
            
                        <div className={styles.actions}>
                            <button
                                className={cn("button", styles.button)}
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Création en cours..." : "Créer le prospect"}
                            </button>
                        </div>
        </Card>
    );
};

export default ContactInformations;